import { useBackgroundQuery, QueryRef } from '@apollo/client/react';
import { useSearchParams } from 'react-router';

import { apolloClient as client } from 'src/graphql/client/apollo-client';
import { createRequiredContext } from 'src/context/createRequiredContext';
import { EscrowStatusFilterEnum } from 'src/graphql/schema';
import {
  SoftwareEscrowsDocument,
  ISoftwareEscrowsQueryVariables,
} from 'src/graphql/actions/softwareEscrow/softwareEscrows.query';
import { ESCROWS_LIMIT } from 'src/shared/constants';

import { setValidStatus } from '../lib';

import type { ISoftwareEscrowsQuery } from 'src/graphql/actions/softwareEscrow/softwareEscrows.query';

import { useDashboardSlice } from './useDashboardSlice';

const [useDashboard, DashboardProvider] = createRequiredContext<
  ReturnType<typeof useDashboardSlice> & {
    queryRef: QueryRef<ISoftwareEscrowsQuery>;
    refetchEscrows: () => void;
    status: string;
    setStatusFilter: (status: string) => void;
  }
>();

const DashboardContextProvider = ({ children }: React.PropsWithChildren) => {
  const dashboardSlice = useDashboardSlice();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = setValidStatus(searchParams.get('t')) || 'all';
  const variables: ISoftwareEscrowsQueryVariables = {
    status: status as EscrowStatusFilterEnum,
    page: dashboardSlice.page,
    perPage: ESCROWS_LIMIT,
    ...(dashboardSlice.query && { query: dashboardSlice.query }),
  };
  const [queryRef] = useBackgroundQuery<ISoftwareEscrowsQuery, ISoftwareEscrowsQueryVariables>(
    SoftwareEscrowsDocument,
    {
      variables,
      fetchPolicy: 'cache-and-network',
    },
  );

  const refetchEscrows = () => {
    client.cache.evict({ fieldName: 'softwareEscrows' });
    client.cache.gc();
    client.refetchQueries({
      include: [SoftwareEscrowsDocument],
    });
  };

  const setStatusFilter = (status: string) => {
    setSearchParams({ t: status });
    dashboardSlice.setPage(1);
  };

  const values = {
    queryRef,
    refetchEscrows,
    status,
    setStatusFilter,
    ...dashboardSlice,
  };

  return <DashboardProvider value={values}>{children}</DashboardProvider>;
};

export { useDashboard, DashboardContextProvider };
